/**
 * SSI Diagnostic Formulation page (4_DF.js).
 *
 * For organisations where the Diagnostic Formulation is deferred to after the
 * Recommended Diagnostic Decision (e.g. The Owl Centre), this page replaces
 * the CI-based DF (12_DF.js).  It lives in the SSI workflow — no CI side-nav.
 *
 * Unlike the traditional DF with PPD/SCD/Differentials, this page presents a
 * simplified layout:
 *
 *   1. A single multi-select "Differentials" panel where the clinician can
 *      select any conditions they are considering (including ADHD).  There is
 *      no primary vs. secondary distinction.
 *
 *   2. A free-text "Formulation Notes" box for draft clinical reasoning.
 *      These notes serve as a working reference for the MDT review in the
 *      Diagnostic Decision and are displayed read-only on the DD page.
 *      They are also visible in the RD_CI.js review side panel under
 *      "Your notes".  By default, they do NOT feed into the AI-generated
 *      ci_DF report paragraph (the paragraph remains deterministic).
 *      This can be toggled via the `notes_feed_into_paragraph` parameter
 *      in the backend generation function if an org wants AI-enriched
 *      paragraphs in the future.
 *
 * Data path: writes `differentialDiagnoses` and `clinicianNotes` to
 * `2_clinicalInterview.sections.12` (the existing DF Firestore path).
 * `provisionalPrimaryDiagnosis` and `secondaryComorbidDiagnoses` are NOT
 * written by this page.
 */

import React, { useState, useMemo, useContext, useCallback, useEffect } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { ChevronLeftIcon, ChevronRightIcon, InformationCircleIcon } from '@heroicons/react/solid';
import Breadcrumb from '../../../components/Breadcrumb';
import { AccordionData } from '../../../constants/data_DF';
import ConditionSelectionPanel from '../6_clinicalInterview/components/ConditionSelectionPanel';
import DictationTextArea from '../../../components/DictationTextArea';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import ModuleCompletionDialog, { CancelButton, PrimaryActionButton, GenerateReportButton, SummarySection, DialogMessage } from '../../../components/ModuleCompletionDialog';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import { auth } from '../../../firebase';
import { isClinicianAssignedToStage } from '../../../utils/clinicianPermissions';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { useSectionExitRegeneration } from '../../../hooks/useSectionExitRegeneration';
import ButtonSpinner from '../../../components/informantManagement/ButtonSpinner';

export default function SSIDiagnosticFormulation() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patientDataModel, updatePatientFirestore, flushPendingUpdates } = useContext(PatientDataContext);
  const { claims } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);

  // Tracks whether the user has made any local edits on this page.
  // Used to conditionally trigger AI paragraph regeneration on exit —
  // if the clinician navigates away without changing anything, there
  // is no need to re-run the (potentially expensive) LLM call.
  const [hasChanges, setHasChanges] = useState(false);

  // Route guard: this page has been superseded by the Clinical Review &
  // Synthesis page (3_SSIReview.js) for deferred-DF orgs. Both deferred-DF
  // and non-deferred orgs are now redirected away — deferred goes to the new
  // /review page, non-deferred goes to Patient Overview. This catches stale
  // bookmarks and direct URL entries.
  useEffect(() => {
    if (!claims) return;
    if (wf.showSSIReviewPage) {
      navigate(`/patient/${patientId}/proforma/semi-structured-interview/review`, { replace: true });
    } else {
      navigate(`/patient/${patientId}`, { replace: true });
    }
  }, [claims, wf.showSSIReviewPage, patientId, navigate]);

  // DF data still lives under the CI path so the backend pipeline is unchanged
  const currentSectionNumber = 12;

  // --- Permissions ---
  // Edit permission is gated on the CI stage assignment because the
  // underlying data model path belongs to the Clinical Interview.
  const [canEdit, setCanEdit] = useState(false);
  useEffect(() => {
    const checkPermissions = async () => {
      const currentUser = auth.currentUser;
      if (currentUser && patientDataModel) {
        const hasEditPermission = isClinicianAssignedToStage(patientDataModel, currentUser.uid, 'CI');
        setCanEdit(hasEditPermission);
      }
    };
    checkPermissions();
  }, [patientDataModel]);

  // When Criterion E was answered "Yes" in RDD (the symptoms are NOT better
  // explained by another disorder, i.e. ADHD is the best explanation), ADHD
  // must be pre-selected in the conditions in consideration and cannot be
  // removed — removing it would contradict the Criterion E decision.
  // This only applies for deferred-DF orgs (CI + SSI in one appointment,
  // e.g. WLC/TOC), NOT for SSI-first orgs like SLaM where the DF in CI
  // is completed independently after the SSI.
  const ADHD_CONDITION = 'Attention Deficit Hyperactivity Disorder (ADHD)';
  const rddCriterionE = patientDataModel?.dataModel?.['3_semiStructuredInterview']?.sections?.['3']?.dsmCriterionE_NotOtherExplanation;
  const adhdLockedByRDD = wf.isDFDeferred && rddCriterionE === true;

  // --- Differentials selection state (initialised from Firestore) ---
  const [differentials, setDifferentials] = useState(() => {
    const sectionData = patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.[currentSectionNumber] || {};
    const saved = sectionData.differentialDiagnoses || [];

    // If RDD Criterion E = Yes and ADHD isn't already in the saved list,
    // inject it so the initial state is consistent with the RDD decision.
    const selected = (adhdLockedByRDD && !saved.includes(ADHD_CONDITION))
      ? [ADHD_CONDITION, ...saved]
      : saved;

    return {
      selected,
      isConfirmed: !!(selected.length),
    };
  });

  // Registers dirty state with ProformaNavigationGuardContext and provides
  // unmount-based regeneration as a safety net for unmanaged exits (breadcrumb,
  // top nav, browser back). markRegenHandled() is called in the central
  // handleManagedNavigation function to prevent double-firing when the
  // managed Next/Finish/Back paths are used. shouldRegenerate is gated on
  // differentials being confirmed — there is nothing meaningful to generate
  // if the clinician hasn't confirmed their condition selection.
  const { markRegenHandled } = useSectionExitRegeneration({
    patientId,
    stageTitle: 'Clinical Interview',
    sectionTitle: 'Diagnostic Formulation',
    hasChanges,
    canEdit,
    patientDataModel,
    shouldRegenerate: differentials.isConfirmed,
  });

  // --- Formulation Notes state ---
  // Lazy initializer reads once on mount; local state is never overwritten by
  // subsequent patientDataModel refreshes (avoids the race where a Firestore
  // write-refresh cycle clobbers text the user has typed but not yet blurred).
  const [additionalNotes, setAdditionalNotes] = useState(() => {
    return patientDataModel?.dataModel?.['2_clinicalInterview']
      ?.sections?.[currentSectionNumber]?.clinicianNotes?.['Additional Notes'] || '';
  });

  // --- Firestore helpers ---
  const sendSectionUpdates = useCallback((newUpdates) => {
    if (!canEdit) return Promise.resolve();
    return updatePatientFirestore(patientId, {
      '2_clinicalInterview': {
        sections: {
          [currentSectionNumber]: newUpdates
        }
      }
    });
  }, [patientId, currentSectionNumber, updatePatientFirestore, canEdit]);

  // --- Differentials handlers ---
  const handleSelect = (value) => {
    if (!canEdit) return;
    if (adhdLockedByRDD && value === ADHD_CONDITION) return;
    setHasChanges(true);
    setDifferentials(prev => {
      const currentSelections = prev.selected;
      const newValues = currentSelections.includes(value)
        ? currentSelections.filter(item => item !== value)
        : [...currentSelections, value];
      return { ...prev, selected: newValues };
    });
  };

  // Prevents the clinician from deselecting ADHD when Criterion E = Yes in RDD.
  const isConditionDisabled = useCallback((conditionLabel) => {
    return adhdLockedByRDD && conditionLabel === ADHD_CONDITION;
  }, [adhdLockedByRDD]);

  const handleConfirm = () => {
    if (!canEdit) return;
    setHasChanges(true);
    setDifferentials(prev => ({ ...prev, isConfirmed: true }));
    sendSectionUpdates({ differentialDiagnoses: differentials.selected });
  };

  const handleChangeChoice = () => {
    if (!canEdit) return;
    setHasChanges(true);
    setDifferentials(prev => ({ ...prev, isConfirmed: false }));
  };

  // --- Notes handlers ---
  const handleAdditionalNotesChange = (value) => {
    if (!canEdit) return;
    setHasChanges(true);
    setAdditionalNotes(value);
  };

  const handleAdditionalNotesBlur = () => {
    if (!canEdit) return;
    sendSectionUpdates({ clinicianNotes: { 'Additional Notes': additionalNotes } });
  };

  // --- Dialog state ---
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // --- RDD recap data for the completion dialog ---
  const rddSection = useMemo(() =>
    patientDataModel?.dataModel?.['3_semiStructuredInterview']?.sections?.['3'] || {},
    [patientDataModel]
  );

  const constructRDDRecap = useCallback(() => {
    const output = [];
    const decision = rddSection.diagnosisDecision;
    output.push(`• Criteria for ADHD: ${decision || 'Not specified'}`);
    if (decision === 'Fully met') {
      if (rddSection.diagnosisPresentation) output.push(`• Presentation: ${rddSection.diagnosisPresentation}`);
      if (rddSection.diagnosisSeverity) output.push(`• Severity: ${rddSection.diagnosisSeverity}`);
    } else if (decision === 'Partially met') {
      if (rddSection.partialDiagnosisClassification) {
        output.push(`• Classification: ${rddSection.partialDiagnosisClassification}`);
      }
    }
    return output.map((bullet, i) => <div key={i} className="mb-1">{bullet}</div>);
  }, [rddSection]);

  // --- Differentials recap for the completion dialog ---
  const constructDifferentialsRecap = useCallback(() => {
    if (differentials.selected.length === 0) {
      return <div className="text-gray-500 italic">No conditions selected</div>;
    }
    return differentials.selected.map((condition, i) => (
      <div key={i} className="mb-1">• {condition}</div>
    ));
  }, [differentials.selected]);

  // --- Report generation state (MDT review orgs only) ---
  const [isGenerating, setIsGenerating] = useState(false);
  const clinicianId = auth.currentUser?.uid;

  // --- Central managed-exit function ---
  // Every navigation path out of this page — Back, Proceed to DD, Finish SSI,
  // and sidebar navigation — funnels through handleManagedNavigation so that
  // we consistently: (1) prevent double-firing from the unmount hook,
  // (2) flush any debounced writes, (3) persist the latest differentials
  // and notes snapshot, and (4) conditionally trigger AI paragraph generation
  // when the clinician has actually changed something on this page AND has
  // confirmed their differentials selection.
  //
  // This mirrors the "central managed-exit" pattern used in SSI symptom /
  // domain panels and PAD pages for hyper-consistency across the platform.
  const isGenerated = !!(patientDataModel?.dataModel?.['6_manageReports']?.sections?.['1']?.stageSectionsGenerated?.includes?.('Diagnostic Formulation'));

  const handleManagedNavigation = useCallback((route) => {
    if (!canEdit) {
      navigate(route);
      return;
    }

    markRegenHandled();

    // Queue the latest differentials and notes BEFORE flushing so they're
    // included in the same Firestore transaction as any pending edits.
    sendSectionUpdates({
      differentialDiagnoses: differentials.selected,
      clinicianNotes: { 'Additional Notes': additionalNotes },
    });

    const dataReady = flushPendingUpdates({ noTimeout: true });

    // Chain AI generation to the flush — fires as soon as data is
    // confirmed committed.  Only generate when the clinician made
    // changes (or first pass) and has confirmed their differentials.
    if ((hasChanges || !isGenerated) && differentials.isConfirmed) {
      TriggerGenerateDocumentSection(patientId, 'Clinical Interview', 'Diagnostic Formulation', [], dataReady);
    }

    navigate(route);
  }, [canEdit, navigate, markRegenHandled, flushPendingUpdates, sendSectionUpdates,
      differentials.selected, differentials.isConfirmed, additionalNotes,
      hasChanges, isGenerated, patientId]);

  // --- Completion flow ---
  // Mark the DF section as complete and open the summary dialog.
  // AI paragraph generation is NOT triggered here — it is deferred to
  // handleManagedNavigation when the clinician actually navigates away
  // (via "Proceed to DD" or "Finish SSI" in the dialog). This avoids
  // a subtle bug: if the clinician completes, cancels the dialog, makes
  // further edits, and then navigates away via breadcrumb, the old
  // handleComplete-based generation would have already fired on stale
  // data and the unmount hook's regeneration would be suppressed.
  const handleComplete = useCallback(async () => {
    if (!canEdit) return;
    setIsSaving(true);
    // Queue the data + status update, then await the flush to ensure
    // Firestore has the final state before showing the dialog.
    sendSectionUpdates({
      differentialDiagnoses: differentials.selected,
      clinicianNotes: { 'Additional Notes': additionalNotes },
      status: 'completed',
      progress: 100,
    });
    await flushPendingUpdates();

    setIsSaving(false);
    setIsDialogOpen(true);
  }, [canEdit, flushPendingUpdates, sendSectionUpdates, differentials.selected, additionalNotes]);

  // After completing the DF, where the clinician goes depends on DD style:
  //
  //  - MDT Review orgs (WLC, TOC): the DD happens in a separate session
  //    (an MDT meeting, not part of this patient-facing appointment).
  //    The SSI report was already generated in the background when the
  //    clinician proceeded from RDD to DF.  Here we show the standard
  //    GenerateReportProgressDialog, then navigate to the Documents
  //    Dashboard where CI + SSI reports await review.
  //
  //  - Standard DD orgs: the clinician proceeds directly to the DD page
  //    in the same session.  Flat-model orgs go to the ADHD-specific
  //    route; hierarchical orgs go to the primary-diagnosis route.
  const handleGenerateReport = useCallback(() => {
    setIsDialogOpen(false);
    setIsGenerating(true);
  }, []);

  const handleCancelGeneration = useCallback(() => {
    setIsGenerating(false);
  }, []);

  // Called by GenerateReportProgressDialog when simulated progress completes.
  // Routes through the central managed-exit so flush + regen logic runs.
  const handleAfterGeneration = useCallback(() => {
    handleManagedNavigation(`/patient/${patientId}/documents`);
  }, [patientId, handleManagedNavigation]);

  const handleProceedToDD = useCallback(() => {
    const ddRoute = wf.isFlatDifferentials
      ? `/patient/${patientId}/proforma/diagnostic-decision/adhd`
      : `/patient/${patientId}/proforma/diagnostic-decision/primary-diagnosis`;
    handleManagedNavigation(ddRoute);
  }, [patientId, wf.isFlatDifferentials, handleManagedNavigation]);

  // --- Navigation ---
  const handleBack = useCallback(() => {
    handleManagedNavigation(`/patient/${patientId}/proforma/semi-structured-interview/recommended-diagnosis`);
  }, [patientId, handleManagedNavigation]);

  // --- Breadcrumb ---
  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}`],
    ['Semi-Structured Interview'],
    ['Diagnostic Formulation']
  ];

  // The forward button is enabled once differentials have been confirmed
  // (including an empty confirmation via allowEmptyConfirm).
  const isFormReady = differentials.isConfirmed;

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />
      <div className="flex-grow p-8">
      <div className="max-w-[70%] mx-auto">
        {/* Top navigation bar */}
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={handleBack}
            disabled={isSaving}
            className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 ${isSaving ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50'}`}
          >
            {isSaving
              ? <ButtonSpinner className="w-5 h-5 mr-2" />
              : <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />}
            <span className="font-bold">Back to Recommended Diagnosis</span>
          </button>
          {canEdit && (
            <button
              onClick={handleComplete}
              className={`flex items-center py-2 px-4 rounded-md shadow-md transition-all duration-300 ${
                isFormReady
                  ? 'hover:bg-violet-800 bg-violet-900 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              disabled={!isFormReady || isSaving}
            >
              <span className="font-bold">
                {isSaving
                  ? 'Saving Progress...'
                  : wf.isMDTReview
                    ? 'Finish Semi-Structured Interview'
                    : 'Proceed to Diagnostic Decision'}
              </span>
              {isSaving
                ? <ButtonSpinner className="w-5 h-5 ml-2" />
                : <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
            </button>
          )}
        </div>

        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Diagnostic Formulation</h2>
        </div>

        {/* Single multi-select Differentials panel — no primary/secondary distinction.
            The clinician selects whichever conditions they are currently considering,
            including ADHD if appropriate. When Criterion E = Yes in RDD, ADHD is
            pre-selected and locked since removing it would contradict the RDD. */}
        <div className="flex flex-col gap-6">
          {adhdLockedByRDD && (
            <div className="rounded-2xl p-4 text-left" style={{ backgroundColor: 'rgba(67, 33, 62, 0.08)' }}>
              <div className="flex items-start">
                <InformationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#43213e' }} />
                <p className="ml-3 text-sm" style={{ color: '#43213e' }}>
                  ADHD has been pre-selected and cannot be removed because Criterion E in the Recommended Diagnosis Decision indicated that the symptoms are best explained by ADHD.
                </p>
              </div>
            </div>
          )}

          <ConditionSelectionPanel
            title="Conditions Under Consideration"
            subtitle="Select any conditions you are currently considering as part of your differential diagnosis. You can select multiple conditions."
            selectedOptions={differentials.selected}
            isMultiSelect={true}
            isConfirmed={differentials.isConfirmed}
            isDisabled={isConditionDisabled}
            onSelect={handleSelect}
            onConfirm={handleConfirm}
            onChangeChoice={handleChangeChoice}
            conditions={AccordionData}
            allowEmptyConfirm={true}
            canEdit={canEdit}
          />
        </div>

        {/* Formulation Notes — free-text draft for MDT discussion reference.
            These notes are displayed read-only on the Diagnostic Decision page
            so the MDT panel can review the assessing clinician's reasoning.
            They are NOT incorporated into any AI-generated report paragraph. */}
        <div className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mt-6">
          <h3 className="font-bold text-lg mb-2">Formulation Notes</h3>
          <p className="text-sm text-gray-500 mb-4">
            Record your clinical reasoning and formulation thinking. These notes will be visible during the MDT review as a reference for discussion, but will not be included in the report.
          </p>
          <DictationTextArea
            value={additionalNotes}
            onChange={(val) => handleAdditionalNotesChange(val)}
            onBlur={handleAdditionalNotesBlur}
            canEdit={canEdit}
            ariaLabel="Diagnostic Formulation notes"
          />
        </div>

        {/* Bottom navigation */}
        <div className="flex justify-end items-center mt-4 pt-4 border-t border-gray-200">
          {canEdit && (
            <button
              onClick={handleComplete}
              className={`flex items-center py-2 px-4 rounded-md shadow-md transition-all duration-300 ${
                isFormReady
                  ? 'hover:bg-violet-800 bg-violet-900 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              disabled={!isFormReady || isSaving}
            >
              <span className="font-bold">
                {isSaving
                  ? 'Saving Progress...'
                  : wf.isMDTReview
                    ? 'Finish Semi-Structured Interview'
                    : 'Proceed to Diagnostic Decision'}
              </span>
              {isSaving
                ? <ButtonSpinner className="w-5 h-5 ml-2" />
                : <ChevronRightIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
            </button>
          )}
        </div>
      </div>
      </div>

      {/* Completion dialog — recaps the RDD outcome and selected differentials.
          For MDT review orgs the SSI report was already generated in the
          background when proceeding from RDD, so the primary action here is
          to go to the Documents Dashboard for review.  For standard DD orgs
          the clinician proceeds directly to the Diagnostic Decision page. */}
      <ModuleCompletionDialog
        open={isDialogOpen && !isGenerating}
        onOpenChange={setIsDialogOpen}
        title={wf.isMDTReview
          ? 'Semi-Structured Interview Complete'
          : 'Diagnostic Formulation Complete'}
        description={wf.isMDTReview
          ? <>Your <span className="font-semibold">Clinical Interview</span> and <span className="font-semibold">Semi-Structured Interview</span> reports are ready for review.</>
          : <>Review your ADHD recommendation and conditions under consideration before proceeding to the <span className="font-semibold">Diagnostic Decision</span>.</>}
        actions={
          <>
            <CancelButton onClick={() => setIsDialogOpen(false)} />
            {wf.isMDTReview ? (
              <GenerateReportButton onClick={handleGenerateReport} />
            ) : (
              <PrimaryActionButton
                label="Proceed to Diagnostic Decision"
                onClick={handleProceedToDD}
              />
            )}
          </>
        }
      >
        {/* RDD recap */}
        <SummarySection title="ADHD Recommendation (from RDD):">
          {constructRDDRecap()}
        </SummarySection>

        {/* Differentials recap */}
        <SummarySection title="Conditions Under Consideration:">
          {constructDifferentialsRecap()}
        </SummarySection>

        {/* Notes preview */}
        {additionalNotes.trim() && (
          <div className="mb-4">
            <h3 className="font-semibold mb-2">Formulation Notes:</h3>
            <div className="text-gray-700 text-sm whitespace-pre-wrap bg-gray-50 rounded-lg p-3 border border-gray-200">
              {additionalNotes}
            </div>
          </div>
        )}

        <DialogMessage>
          {wf.isMDTReview
            ? 'Your formulation notes and conditions under consideration will be available as a reference during the MDT review.'
            : <>Your formulation notes will be available as a reference during the <span className="font-semibold not-italic">Diagnostic Decision</span>.</>}
        </DialogMessage>
      </ModuleCompletionDialog>

      {/* Report generation progress dialog — shown for MDT review orgs
          after the clinician clicks "Generate Report".  The SSI report
          was already triggered in the background from RDD; this dialog
          provides the standard simulated-progress UX before navigating
          to the Documents Dashboard. */}
      <GenerateReportProgressDialog
        reportStage="Semi-Structured Interview"
        patientId={patientId}
        clinicianId={clinicianId}
        handleNextPageButtonClick={handleAfterGeneration}
        isGenerating={isGenerating}
        onCancel={handleCancelGeneration}
      />
    </Layout>
  );
}
